/**
 * Appointment API Service
 *
 * REST API wrapper for /api/appointments endpoints.
 * Transforms between frontend camelCase and backend snake_case.
 */

import api from './api';
import type {
  Appointment,
  BackendAppointment,
  AppointmentFilters,
  CreateAppointmentInput,
  UpdateAppointmentInput,
  HoldUpdateInput,
  FeeUpdateInput,
  StatusTransitionsMap,
} from '../types/appointment';

/**
 * What the extension should look for on eModal when an appointment was
 * recorded here by hand and we don't yet hold eModal's own IDs for it.
 */
export interface EmodalLinkSearch {
  containerNumber: string | null;
  ticket: string | null;
  terminalCode: string;
  mtoId: number | null;
  mtoDisplay: string | null;
  /** eModal's apptType_Cd — IP / EP / BD / MD. Tells two appointments for the same container apart. */
  apptTypeCd: string | null;
  apptTypeLabel: string | null;
  expectedFrom: string | null;
  expectedTo: string | null;
}

/**
 * Response of the fix-plate / reschedule session endpoints. `mode`
 * discriminates: 'edit' carries the eModal deep link, 'link_required'
 * carries where to go and what to look for so the extension can acquire
 * the IDs first.
 */
export type EmodalActionSession =
  | {
      mode: 'edit';
      sessionId: string;
      url: string;
      appointmentId: number;
      newPlate?: string | null;
      oldPlate?: string | null;
      /**
       * Passed straight through to the extension. eModal renders the same
       * empty wizard whether the deep link resolved or not, so the assistant
       * needs one value it can look for to tell the two apart.
       */
      containerNumber?: string | null;
      currentAppointmentTime?: string | null;
      currentAppointmentEnd?: string | null;
    }
  | {
      mode: 'link_required';
      intent: 'fix_plate' | 'reschedule';
      sessionId: string;
      appointmentId: number;
      listUrl: string;
      search: EmodalLinkSearch;
      newPlate?: string | null;
      oldPlate?: string | null;
      currentAppointmentTime?: string | null;
      currentAppointmentEnd?: string | null;
    };

/**
 * 改车牌比改期多一种答案：**什么都不用改**。
 *
 * 那一队是在点确认那一刻排好的，真正开窗口却是后面一张张来的；中间约票那一下、
 * 同一张票另一程的改牌、或者一次同步，都可能已经把车牌写对了。后端开窗口之前
 * 会再确认一次（`plateAlreadyCorrect`），对上了就回这一条，前端当场算完成。
 *
 * 单独起一个类型而不是塞进 `EmodalActionSession`：改期那条路永远收不到它，
 * 把它并进公用的联合里，只会让改期那边每用一个字段都要先排除一种不可能的情况。
 */
export type FixPlateSession =
  | EmodalActionSession
  | {
      mode: 'already_correct';
      appointmentId: number;
      newPlate?: string | null;
      oldPlate?: string | null;
    };

/** IDs scraped off an existing eModal appointment, sent back to be persisted. */
export interface EmodalLinkPayload {
  truckVisitId: number | string;
  gateApptId: number | string;
  mtoId: number | string;
  container?: string | null;
  ticket?: string | null;
  truckPlate?: string | null;
  visitFrom?: string | null;
}

export interface EmodalLinkWarning {
  code: string;
  message: string;
}

/**
 * Shape returned by POST /api/appointments/booking-session.
 * The backend assembles terminal-profile-driven form data; the extension
 * doesn't inspect most of this beyond passing fields back to eModal, so the
 * interior is left loosely typed.
 */
export interface BookingSessionPayload {
  sessionId: string;
  /**
   * The backend handed back a booking that was already live for this task
   * instead of opening a second one. One task, one eModal wizard — a duplicate
   * click returns to the tab already open rather than racing itself into two
   * real appointments at the terminal.
   */
  reused?: boolean;
  profileVersion: number;
  profileHash: string;
  terminal_code: string;
  terminal_name: string;
  move_type: string;
  subdomain: string;
  make_appointment_url: string;
  wizard: unknown;
  fields: Array<{
    name: string;
    wizard_step: number;
    required: boolean;
    value: string | number | null;
    selector: unknown;
    input_method: string | null;
    validate: unknown;
  }>;
  receipt_scrape: unknown;
  appointment_window_minutes: number;
  check_in_lead_minutes: number;
  /**
   * 出口还重柜才有：开向导**之前**要先把这几条订舱号的闸口费申领下来
   * （没申领到就进不了闸）。插件据此多走一步，页面这边只用它判断
   * 「这一单该多给多少耐心」—— 那一步要等人在 eModal 上按一下。
   */
  export_gate_fee_claim?: {
    facility_cd: string | null;
    terminal_code: string | null;
    bookings: string[];
  } | null;
  /**
   * 批量预约才有：一次向导里放进去的每只柜，以及它自己那份字段。
   * 只有一只柜时后端不会带这一段，形状和以前完全一样。
   */
  batch?: {
    count: number;
    moves: Array<{
      task_id: number;
      seq: number;
      is_primary: boolean;
      container_number: string;
      fields: BookingSessionPayload['fields'];
    }>;
  };
}

/** 批量预约弹窗里的一行。 */
export interface BatchBookingCandidate {
  taskId: number;
  taskNumber: string;
  /** 出口柜在提柜之前没有柜号 —— 那种情况下这里是 null，屏幕上显示订舱号。 */
  containerNumber: string | null;
  /** 出口订舱号。出口在码头认的就是这个号。 */
  bookingNumber: string | null;
  containerSize: string | null;
  /** 这只柜算几呎（40HC→40）。码头一张票的柜子加起来不能超 maxTotalFeet。 */
  sizeFeet: number | null;
  /** 'reefer' | 'dry'；**可以是空**，空就不画那一枚（判法见 utils/containerTypeLabel）。 */
  containerType: string | null;
  clientName: string | null;
  lfd: string | null;
  terminalCode: string;
  terminalName: string;
  moveType: string;
  /** 调度是点着这只柜进来的，弹窗里默认勾上、排第一。 */
  isAnchor: boolean;
  /** 这只柜差点东西约不了（比如 ETS 要 PIN 但订单没提单号）。勾选框禁用。 */
  blocked: boolean;
  blockedReason: string | null;
}

export interface BatchBookingCandidates {
  terminalCode: string;
  terminalName: string;
  moveType: string;
  /** 这一批在码头认哪个号：进口认柜号，出口认订舱号。 */
  identity: 'container' | 'booking';
  /** identity='booking' 时，锚点那张订舱号（只用于显示，不限制选择）。 */
  bookingNumber: string | null;
  maxPerBatch: number;
  /**
   * 一张票的柜子加起来最多多少呎（出口 80；进口不适用，为 null）。
   * 依据是码头自己的 validateMoves()：超了在第 2 步 Next 上被顶回来。
   */
  maxTotalFeet: number | null;
  /** 候选太多，只算了前一批的可约性 —— 界面上要说一声。 */
  truncated: boolean;
  candidates: BatchBookingCandidate[];
}

/**
 * Returned when the task is not eligible for eModal auto-booking — e.g. the
 * port isn't in the supported terminal list (BNSF, rail ramps) or required
 * input is missing (ETS PIN needs a BOL). This is a routine business outcome
 * and comes back as HTTP 200, not 400, so the extension booker can gracefully
 * fall through to the manual modal without treating it as a failure.
 */
export interface BookingSessionNotSupported {
  notSupported: true;
  code: string;
  message: string;
  /** `reason` 是排错用的长句；`label` 是给界面看的短标签（如 "No BOL Number"）。 */
  blockers?: Array<{ field: string; reason: string; label?: string; code: string }> | null;
}

export type BookingSessionResult = BookingSessionPayload | BookingSessionNotSupported;

export function isBookingSessionSupported(
  r: BookingSessionResult
): r is BookingSessionPayload {
  return !(r as BookingSessionNotSupported).notSupported;
}

// ── Transform helpers ──

function transformFromBackend(row: BackendAppointment): Appointment {
  return {
    id: row.id,
    taskId: row.task_id,
    orderId: row.order_id,
    sequence: row.sequence,
    facilityType: row.facility_type as Appointment['facilityType'],
    facilityId: row.facility_id,
    facilityName: row.facility_name,
    moveType: row.move_type as Appointment['moveType'],
    appointmentTime: row.appointment_time,
    appointmentEnd: row.appointment_end,
    allDay: !!row.all_day,
    status: row.status as Appointment['status'],
    appointmentNumber: row.appointment_number,
    pendingTaskType: row.pending_task_type,
    containerNumber: row.container_number,
    containerSize: row.container_size,
    chassisNumber: row.chassis_number,
    scacCode: row.scac_code,
    referenceNumber: row.reference_number,
    referenceType: row.reference_type as Appointment['referenceType'],
    isHold: row.is_hold,
    holdReasons: row.hold_reasons,
    feeDetails: row.fee_details,
    feesCleared: row.fees_cleared,
    externalId: row.external_id,
    source: row.source as Appointment['source'],
    lastSyncedAt: row.last_synced_at,
    truckLicensePlate: row.truck_license_plate,
    driverName: row.driver_name,
    rescheduleCount: row.reschedule_count,
    originalAppointmentTime: row.original_appointment_time,
    transferredFromAppointmentId: row.transferred_from_appointment_id,
    pairedAppointmentId: row.paired_appointment_id,
    gateCode: row.gate_code,
    pin: row.pin ?? null,
    locationType: row.location_type,
    terminalName: row.terminal_name,
    containerLocation: row.container_location,
    qrCodeUrl: row.qr_code_url,
    terminalInstructions: row.terminal_instructions,
    lfd: row.lfd,
    steamshipLine: row.steamship_line,
    appointmentRequired: row.appointment_required,
    warehouseNotes: row.warehouse_notes,
    bookedBy: row.booked_by,
    bookedAt: row.booked_at,
    confirmedAt: row.confirmed_at,
    completedAt: row.completed_at,
    notes: row.notes,
    // Migration 098 — eModal IDs come back as either number or string
    // depending on Postgres BIGINT serialisation; coerce to number for
    // arithmetic/comparison, but preserve null.
    emodalTruckVisitId: row.emodal_truck_visit_id != null ? Number(row.emodal_truck_visit_id) : null,
    emodalGateApptConId: row.emodal_gate_appt_con_id != null ? Number(row.emodal_gate_appt_con_id) : null,
    emodalMtoId: row.emodal_mto_id ?? null,
    plateVerifiedAt: row.plate_verified_at,
    needsRebook: !!row.needs_rebook,
    rebookReason: row.rebook_reason ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    // Calendar JOIN fields (optional — only present from getByDateRange)
    taskBid: row.task_bid,
    driverId: row.driver_id,
    taskStatus: row.task_status,
    // B3 — populated by getByTaskId.
    currentDriverTruckPlate: row.current_driver_truck_plate ?? null,
    emodalLinkable: row.emodal_linkable ?? undefined,
    // 后端算出来的「这张票在码头那边已经没了」。undefined = 这条查询没算
    // （只有 getByTaskId 算），不要当成 false 用。
    ticketNumberStale: row.ticket_number_stale ?? undefined,
  };
}

function transformToBackend(
  data: CreateAppointmentInput | UpdateAppointmentInput
): Record<string, unknown> {
  const map: Record<string, string> = {
    facilityType: 'facility_type',
    moveType: 'move_type',
    taskId: 'task_id',
    orderId: 'order_id',
    facilityId: 'facility_id',
    facilityName: 'facility_name',
    appointmentTime: 'appointment_time',
    appointmentEnd: 'appointment_end',
    allDay: 'all_day',
    appointmentNumber: 'appointment_number',
    containerNumber: 'container_number',
    containerSize: 'container_size',
    chassisNumber: 'chassis_number',
    scacCode: 'scac_code',
    gateCode: 'gate_code',
    containerLocation: 'container_location',
    isHold: 'is_hold',
    holdReasons: 'hold_reasons',
    feeDetails: 'fee_details',
    feesCleared: 'fees_cleared',
    warehouseNotes: 'warehouse_notes',
    referenceNumber: 'reference_number',
    referenceType: 'reference_type',
  };
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) continue;
    result[map[key] || key] = value;
  }
  return result;
}

function buildParams(filters: AppointmentFilters): URLSearchParams {
  const params = new URLSearchParams();
  const map: Record<string, string> = {
    taskId: 'task_id',
    orderId: 'order_id',
    facilityType: 'facility_type',
    facilityId: 'facility_id',
    moveType: 'move_type',
    containerNumber: 'container_number',
    dateFrom: 'date_from',
    dateTo: 'date_to',
    activeOnly: 'active_only',
    sortBy: 'sort_by',
    sortOrder: 'sort_order',
  };
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(map[key] || key, String(value));
  }
  return params;
}

// ── API ──

/**
 * 助手约不了这个码头的那一张。
 *
 * 既不是「不用约」（柜子到闸口还是要有票），也没排进队列（排了必然失败）。
 * 界面拿到它要做的事：当场报警 → 把人领到那张任务的 Route & Appt 去填 →
 * 填了就派，没填就留在池子里并把柜号报出来。
 */
export interface NeedsManualAppt {
  taskId: number;
  pairedTaskId: number | null;
  driverId: number | null;
  containerNumber: string | null;
  terminalCode: string | null;
  terminalName: string | null;
  reason: string;
}

/**
 * 预约批次计划里的一项。数组次序 = 屏幕上从上到下的次序（后端照单去约）。
 */
export interface BookingPlanItem {
  taskId: number;
  driverId: number | null;
  /**
   * 调度**这一趟把它拖到了哪一天**（'YYYY-MM-DD'），没拖过就不带。
   *
   * 后端只拿它判一件事：这张已经生效的票要不要改期。不能让后端回头去读
   * `tasks.driver_sort_day` —— 那一列从 2026-09-22 起每次 Confirm 都会写
   * （司机卡要照它排），装的多半是算出来的日子而不是人拖出来的。
   */
  plannedDay?: string | null;
}

/**
 * 这一轮被**自动解掉**的一还一提。
 *
 * 一还一提＝一次进闸办两件事，前提是同一个司机、同一家码头、一还一提两个方向。
 * 改码头、改派司机、哪一边跑完或取消之后这层关系就不成立了，后端会就地解掉 ——
 * 不解的话下一次派车会因为「搭档不在这次计划里」把整单静默跳过，车派出去了
 * 却没有票（线上 WHSU7389272，2026-10-02）。
 */
export interface UnpairedPair {
  taskId: number;
  pairedTaskId: number;
  /** MIXED_TERMINAL / DRIVER_SPLIT / LEG_FINISHED / NO_TERMINAL_LEG / SAME_DIRECTION / SPLIT_TICKETS */
  code: string;
  /** 给人看的一句话（英文，后端出） */
  reason: string;
  containerNumber: string | null;
  pairedContainerNumber: string | null;
}

export interface BookingBatchCreated {
  /** 整批都要人工约时没有批次可建，这里是 null。 */
  batchId: number | null;
  queued: number;
  /** 不用约的那几张，带原因 —— 界面要显示，否则看着像丢单。 */
  skipped: Array<{ taskId: number; reason: string; code?: string | null }>;
  /** 码头不支持自动约票、预约还空着的那几张。 */
  needsManualAppt: NeedsManualAppt[];
  /** 这一轮顺手解掉的一还一提（可能没有）。 */
  unpaired?: UnpairedPair[];
}

export interface BookingBatchPreflight {
  queued: Array<{ taskId: number; pairedTaskId: number | null }>;
  skipped: Array<{ taskId: number; reason: string; code?: string | null }>;
  needsManualAppt: NeedsManualAppt[];
  unpaired?: UnpairedPair[];
}

export interface BookingBatchProgress {
  batchId: number;
  status: 'planning' | 'running' | 'settled' | 'cancelled';
  total: number;
  booked: number;
  failed: number;
  uncertain: number;
  queued: number;
  inProgress: number;
  skipped: number;
  items: Array<{
    id: number;
    task_id: number;
    paired_task_id: number | null;
    status: string;
    outcome_reason: string | null;
    container_number: string | null;
    paired_container_number: string | null;
  }>;
}

export const appointmentApi = {
  /** Get all appointments with optional filters */
  async getAll(filters: AppointmentFilters = {}): Promise<Appointment[]> {
    const params = buildParams(filters);
    const qs = params.toString();
    const response = await api.get(`/appointments${qs ? `?${qs}` : ''}`);
    return (response.data as BackendAppointment[]).map(transformFromBackend);
  },

  /** Get a single appointment by ID */
  async getById(id: number): Promise<Appointment> {
    const response = await api.get(`/appointments/${id}`);
    return transformFromBackend(response.data as BackendAppointment);
  },

  /** Get all appointments for a specific task */
  async getByTaskId(taskId: number): Promise<Appointment[]> {
    const response = await api.get(`/appointments/task/${taskId}`);
    return (response.data as BackendAppointment[]).map(transformFromBackend);
  },

  /** Get appointments for calendar view */
  async getCalendar(
    dateFrom: string,
    dateTo: string,
    filters: { facilityType?: string; facilityId?: number; includeCancelled?: boolean } = {}
  ): Promise<Appointment[]> {
    const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
    if (filters.facilityType) params.set('facility_type', filters.facilityType);
    if (filters.facilityId) params.set('facility_id', String(filters.facilityId));
    if (filters.includeCancelled) params.set('include_cancelled', 'true');
    const response = await api.get(`/appointments/calendar?${params.toString()}`);
    return (response.data as BackendAppointment[]).map(transformFromBackend);
  },

  /** Create a new appointment */
  async create(data: CreateAppointmentInput): Promise<Appointment> {
    const body = transformToBackend(data);
    const response = await api.post('/appointments', body);
    return transformFromBackend(response.data as BackendAppointment);
  },

  /** Update an appointment */
  async update(id: number, data: UpdateAppointmentInput): Promise<Appointment> {
    const body = transformToBackend(data);
    const response = await api.put(`/appointments/${id}`, body);
    return transformFromBackend(response.data as BackendAppointment);
  },

  /** Delete an appointment */
  async remove(id: number): Promise<void> {
    await api.delete(`/appointments/${id}`);
  },

  /** Set or clear hold on an appointment */
  async setHold(id: number, data: HoldUpdateInput): Promise<Appointment> {
    const response = await api.put(`/appointments/${id}/hold`, {
      is_hold: data.isHold,
      hold_reasons: data.holdReasons,
    });
    return transformFromBackend(response.data as BackendAppointment);
  },

  /** Update fee details on an appointment */
  async updateFees(id: number, data: FeeUpdateInput): Promise<Appointment> {
    const response = await api.put(`/appointments/${id}/fees`, {
      fee_details: data.feeDetails,
      fees_cleared: data.feesCleared,
    });
    return transformFromBackend(response.data as BackendAppointment);
  },

  /** Pair two appointments (bidirectional) */
  async pair(id: number, pairedId: number): Promise<Appointment> {
    const response = await api.post(`/appointments/${id}/pair`, { paired_id: pairedId });
    return transformFromBackend(response.data as BackendAppointment);
  },

  /** Unpair an appointment */
  async unpair(id: number): Promise<Appointment> {
    const response = await api.delete(`/appointments/${id}/pair`);
    return transformFromBackend(response.data as BackendAppointment);
  },

  /** Get valid status transitions map */
  async getTransitions(): Promise<StatusTransitionsMap> {
    const response = await api.get('/appointments/transitions');
    return response.data as StatusTransitionsMap;
  },

  /**
   * eModal sync — ask where this account's appointment list lives.
   *
   * The URL is on a different host from the booking wizard and comes from the
   * terminal profiles, so only the backend can answer it. The extension takes
   * the reply from here, reads the list, and posts it back for reconciliation;
   * the result reaches the console over the `emodal_sync_completed` socket
   * event rather than as a response to anything the page called.
   */
  async createEmodalSyncSession(): Promise<{ sessionId: string; listUrl: string }> {
    const response = await api.post('/appointments/emodal-sync-session');
    return response.data as { sessionId: string; listUrl: string };
  },

  /**
   * B3 Fix Plate — ask the backend what to do for this appointment.
   *
   * Two answers, discriminated by `mode`:
   *   'edit'          — we hold eModal's IDs, here is the deep link.
   *   'link_required' — the appointment was recorded here by hand, so
   *                     the extension first has to find it on eModal
   *                     (search context comes back in `search`) and call
   *                     linkEmodal() with the IDs it captures.
   */
  async createFixPlateSession(id: number): Promise<FixPlateSession> {
    const response = await api.post(`/appointments/${id}/fix-plate-session`);
    return response.data as FixPlateSession;
  },

  /**
   * Attach an existing eModal appointment to one of ours. Called after
   * the extension has seen that appointment's receipt on eModal.
   * Backend refuses a payload whose container/terminal doesn't match the
   * task, so a wrong pick fails loudly instead of silently mis-linking.
   */
  async linkEmodal(
    id: number,
    payload: EmodalLinkPayload
  ): Promise<{ appointment: Appointment; warnings: EmodalLinkWarning[] }> {
    const response = await api.post(`/appointments/${id}/emodal-link`, payload);
    const data = response.data as { appointment: BackendAppointment; warnings?: EmodalLinkWarning[] };
    return {
      appointment: transformFromBackend(data.appointment),
      warnings: data.warnings || [],
    };
  },

  /**
   * B3 Fix Plate — confirm the new plate after the operator submitted on
   * eModal. Body: { verifiedPlate } from the receipt's TruckPlate field.
   * Backend updates appointment.truck_license_plate + plate_verified_at.
   */
  async completeFixPlate(id: number, verifiedPlate: string): Promise<Appointment> {
    const response = await api.put(`/appointments/${id}/fix-plate-complete`, {
      verified_plate: verifiedPlate,
    });
    return transformFromBackend(response.data as BackendAppointment);
  },

  /**
   * Reschedule — request the same eModal edit-mode URL Fix Plate uses,
   * but the operator changes the time slot on Step 3 instead of plate.
   * Returns the URL + the current appointment_time so the hook can
   * detect the completion socket by comparing.
   */
  async createRescheduleSession(id: number): Promise<EmodalActionSession> {
    const response = await api.post(`/appointments/${id}/reschedule-session`);
    return response.data as EmodalActionSession;
  },

  /**
   * Start a booking session for a task (eModal-assisted booking flow).
   *
   * The backend resolves the terminal profile + task context, returns a
   * session id plus the fully-resolved form payload the Chrome extension
   * needs to auto-fill eModal. Caller forwards the payload to the extension
   * via chrome.runtime.sendMessage('EMODAL_BOOKING_START', ...).
   *
   * Rejects with a descriptive error (propagated from api.ts interceptor) when:
   *  - Task not found / wrong tenant
   *  - Port is not a supported eModal terminal (code: 'NOT_SUPPORTED')
   *  - Required fields missing (DRIVER_OR_TRUCK_MISSING / DERIVED_INPUT_MISSING)
   */
  /**
   * @param nodeIndex 这一程是任务路线上的第几个码头节点。Live Load 的两个码头
   *        节点在同一张任务上，动作相反（提重 vs 还空），后端要靠它判动作、
   *        也要靠它决定回执写回哪一程。不传就是 0（提柜那一程）。
   */
  async createBookingSession(taskId: number, nodeIndex = 0): Promise<BookingSessionResult> {
    const response = await api.post('/appointments/booking-session', { taskId, nodeIndex });
    return response.data as BookingSessionResult;
  },

  /**
   * 批量预约：一次向导里约多只柜。
   *
   * 硬约束在后端：同一个码头、同一个动作、最多 10 只柜（eModal 向导第 1 步
   * Terminal 和 Move Type 都是单选，这不是我们的规矩）。违反了会返回
   * notSupported + MIXED_TERMINAL / MIXED_MOVE_TYPE / BATCH_TOO_LARGE。
   */
  /**
   * 预约批次：点一次 Confirm，把「这一批要约什么、什么次序」交给后端。
   *
   * 和 createBatchBookingSession 不是一回事（名字很像，别搞混）：
   *   · createBatchBookingSession = **一次向导**里塞 N 只柜（同码头同动作）
   *   · 这个           = 一次 Confirm 里要**依次**约的 N 项，一项一次向导
   *
   * 返回的 `skipped` 要用起来：那是「这几张不用约」的名单和理由（改派、不去
   * 码头、已经有约）。不显示的话它们在界面上就这么消失了，看着像丢单。
   */
  async createBookingBatch(
    plan: BookingPlanItem[]
  ): Promise<BookingBatchCreated> {
    const response = await api.post('/appointments/booking-batches', { plan });
    return response.data as BookingBatchCreated;
  },

  /**
   * 只分类，不建批次、不锁任务。
   *
   * 用在「人工填完预约之后再问一次能不能派」这一步。判据必须和建批次同源 ——
   * 前端自己看一眼 appointments 数组就下结论的话，迟早和后端分叉，
   * 而分叉的表现是柜子被派出去了却没有票。
   */
  async preflightBookingBatch(
    plan: BookingPlanItem[]
  ): Promise<BookingBatchPreflight> {
    const response = await api.post('/appointments/booking-batches/preflight', { plan });
    return response.data as BookingBatchPreflight;
  },

  /** 批次进度。收尾摘要也用它。 */
  async getBookingBatch(batchId: number): Promise<BookingBatchProgress> {
    const response = await api.get(`/appointments/booking-batches/${batchId}`);
    return response.data as BookingBatchProgress;
  },

  /**
   * 停下这一批。
   *
   * 这是**人的出口**：没有它，一个卡住的批次会一直锁着那几张任务（后端已经
   * 不按时间自动解锁了）。正在约的那一项落成「待确认」并照样派车 ——
   * submit 可能已经发出去，码头那边可能有真票，退回池子会导致重约＝第二张票。
   */
  async abortBookingBatch(batchId: number, reason?: string): Promise<unknown> {
    const response = await api.post(`/appointments/booking-batches/${batchId}/abort`,
      { reason: reason || 'Dispatcher stopped this batch from the board.' });
    return response.data;
  },

  async createBatchBookingSession(taskIds: number[]): Promise<BookingSessionResult> {
    const response = await api.post('/appointments/booking-session', { taskIds });
    return response.data as BookingSessionResult;
  },

  /**
   * 从这只柜出发，能和它一起约的柜有哪些（同码头同动作、released、还没预约）。
   * 不适用时返回 notSupported，调用方退回单柜手工流程。
   */
  async getBatchCandidates(
    taskId: number
  ): Promise<BatchBookingCandidates | BookingSessionNotSupported> {
    const response = await api.get('/appointments/batch-candidates', { params: { taskId } });
    return response.data as BatchBookingCandidates | BookingSessionNotSupported;
  },
};

export default appointmentApi;
